// @ts-strict-ignore
import { useCallback, useState, useMemo, useEffect } from 'react'
import {
  Platform,
  Share,
  type ShareAction,
  type ShareContent,
  type ShareOptions,
} from 'react-native'
import { isWeb } from 'APP/Helpers/checkPlatform'
import Config from 'APP/Config'
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'
import { appendQueryParams } from 'APP/Helpers/UrlBuilder'

type ConfigKey = keyof typeof Config

export const copyToClipboard = (value: string = ''): void => {
  navigator.clipboard.writeText(value)
}

export const useShare = (content?: ShareContent, shareOptions?: ShareOptions) => {
  const [title, _setTitle] = useState<string>()
  const [message, _setMessage] = useState<string>()
  const [url, _setUrl] = useState<string>()
  const [sharing, _setSharing] = useState(false)
  const [options, setOptions] = useState<ShareOptions | undefined>(shareOptions)
  const [results, _setResults] = useState<ShareAction[]>([])
  const [error, _setError] = useState<unknown>()

  const setTitle = (value: string | undefined) => {
    _setTitle(value?.length ? value : undefined)
  }

  const setMessage = (value: string | undefined) => {
    _setMessage(value?.length ? value : undefined)
  }

  const setUrl = useCallback(
    (value: string | undefined) => {
      let parsedURL: string | undefined
      if (!!value) {
        parsedURL = new URL(value).toString()
        parsedURL = parsedURL.endsWith('/') ? parsedURL.slice(0, -1) : parsedURL
      }
      _setUrl(parsedURL ?? url)
    },
    [url]
  )

  useEffect(() => {
    content?.title && setTitle(content.title)
    content?.message && setMessage(content.message)
    content?.url && setUrl(content.url)
  }, [])

  const share = useCallback(async () => {
    try {
      _setError(undefined)
      _setSharing(true)
      if (isWeb()) {
        copyToClipboard(url)
      } else {
        if (!!message || !!url) {
          const content = {
            title,
            message,
            url,
          }
          // @ts-expect-error content has to include truthy message or url,
          // we already checked for having at least one of them, but TS doesn't know that
          const result = await Share.share(content, options)
          _setResults([result, ...results])
          return results[0]
        } else {
          const err = 'The content has to include valid message and/or valid url.'
          _setError(err)
          throw err
        }
      }
    } catch (err) {
      _setError(err)
    } finally {
      _setSharing(false)
    }
  }, [title, message, url, options, results])

  return {
    title,
    setTitle,
    message,
    setMessage,
    url,
    setUrl,
    options,
    setOptions,
    share,
    isSharing: sharing,
    result: useMemo(() => results[0], [results]),
    results: results,
    error,
  }
}

export const useShareDeepLink = (
  baseURL: string | URL | undefined,
  params: Record<string, unknown> = {},
  options: { analyticsName?: string } = {}
) => {
  const { analyticsName } = options
  const deepLinkURL = useMemo(() => {
    return Object.entries(params)
      .filter(([, value]) => value !== null && value !== undefined)
      .reduce(
        (url, [key, value]) => appendQueryParams(url, { [key]: value }),
        new URL('', baseURL).toString()
      )
      .toString()
  }, [baseURL, params])

  const share = Platform.select({
    ios() {
      /* TODO: https://dialoguemd.atlassian.net/browse/DIA-83884 */
      /* eslint-disable-next-line react-hooks/rules-of-hooks */
      return useShare({ url: deepLinkURL })
    },
    android() {
      /* TODO: https://dialoguemd.atlassian.net/browse/DIA-83884 */
      /* eslint-disable-next-line react-hooks/rules-of-hooks */
      return useShare({ message: deepLinkURL })
    },
    default() {
      /* TODO: https://dialoguemd.atlassian.net/browse/DIA-83884 */
      /* eslint-disable-next-line react-hooks/rules-of-hooks */
      return useShare({ url: deepLinkURL })
    },
  })()

  useEffect(() => {
    Platform.select({
      ios() {
        share.setUrl(deepLinkURL)
      },
      android() {
        share.setMessage(deepLinkURL)
      },
      default() {
        share.setUrl(deepLinkURL)
      },
    })()
  }, [deepLinkURL, share])

  useEffect(() => {
    if (share.result && analyticsName) {
      Analytics.trackEvent('button_click', {
        button_value: share.result.action,
        trigger: share.result.activityType,
        screen: analyticsName,
      })
    }
  }, [share.result, analyticsName])

  return share
}

export const useShareDeepLinkFromConfig = (
  configKeyOrConfigHashOrUrl: ConfigKey,
  params: Record<string, unknown>,
  options?: { analyticsName: string; locale?: string },
  config = Config
) => {
  const { analyticsName, locale } = options || {}
  const [baseURL] = useState(() => {
    const locales = [locale || I18n.locale, I18n.locale, I18n.baseLocale]
    return locales.reduce(
      (url: string | undefined, locale: string | undefined, index, confHash) => {
        if (url) {
          return url
        } else if (
          config[configKeyOrConfigHashOrUrl] &&
          typeof config[configKeyOrConfigHashOrUrl] === 'string'
        ) {
          return new URL('', config[configKeyOrConfigHashOrUrl]).toString()
        } else if (
          config[configKeyOrConfigHashOrUrl] &&
          locale &&
          Object(config[configKeyOrConfigHashOrUrl])[locale]
        ) {
          return new URL('', Object(config[configKeyOrConfigHashOrUrl])[locale]).toString()
        } else if (index + 1 >= confHash.length) {
          throw new Error(
            `Conf[${config[configKeyOrConfigHashOrUrl]}][${locales.join('|')}] not defined`
          )
        } else {
          return undefined
        }
      },
      undefined
    )
  })
  return useShareDeepLink(baseURL, params, { analyticsName })
}
